import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "state/store/auth/useAuthStore";
import { useJoinGroup } from "features/group-chat/hooks/use-invite";
import { Loader2 } from "lucide-react";
import { useToast } from "hooks/use-toast";
import { useMobileDeepLink } from 'hooks/use-mobile-deep-link';

export default function GroupInvitePage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { toast } = useToast();
    const { isAuthenticated } = useAuthStore();
    const hasAttemptedJoin = useRef(false);
    const deepLinkUrl = `${process.env.REACT_APP_PUBLIC_MOBILE_BUNDLE_ID}://login/?token=${token}`;
    const [showWebUi, setShowWebUi] = useState(false);
    const [isError, setIsError] = useState(false);

    const { isMobile } = useMobileDeepLink({
        deepLinkUrl,
        fallbackDelay: 2000,
    });

    useEffect(() => {
        setShowWebUi(!isMobile);
        const timer = setTimeout(() => {
            setShowWebUi(true);
        }, 2000);
        return () => clearTimeout(timer);
    }, [isMobile]);

    const { mutate: joinGroup, isPending } = useJoinGroup({
        onSuccess: (data) => {
            if (data?.conversationId) {
                navigate(`/chat/${data.conversationId}`);
            } else {
                navigate('/chat');
            }
            setIsError(false);
        },
        onError: (error) => {
            const errorData = error?.error as any;
            toast({
                variant: 'destructive',
                title: t('SOMETHING_WENT_WRONG'),
                description: errorData?.detail ?? t('FAILED_TO_JOIN_GROUP'),
            });
            setIsError(true);
        }
    });

    useEffect(() => {
        if (!showWebUi) return;
        if (!isAuthenticated) {
            const redirectUrl = location.pathname;
            navigate(`/login?goto=${encodeURIComponent(redirectUrl)}`, { replace: true });
        } else if (token && !hasAttemptedJoin.current) {
            hasAttemptedJoin.current = true;
            joinGroup(token);
        }
    }, [showWebUi, isAuthenticated, location, navigate, token, joinGroup]);

    if (isError) {
        return (
            <div className="flex w-screen h-screen flex-col items-center justify-center gap-4">
                <h1 className="text-xl font-bold text-destructive">{t('ERROR')}</h1>
                <p>{t('FAILED_TO_JOIN_GROUP')}</p>
                <button
                    onClick={() => navigate('/home')}
                    className={`rounded px-4 py-2 bg-primary text-[#FFFFFF]`}
                >
                    {t('GO_HOME', 'Go Home')}
                </button>
            </div>
        )
    }

    if ((!isAuthenticated || isPending || !showWebUi)) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">{t('JOINING_GROUP', 'Joining group...')}</span>
            </div>
        );
    }

    return null;
}